// import { internalAxios } from '@/plugins/vueAxios'
import {
  type CalendarWindowType,
  type LoadCalendarResponse,
  WindowStatus,
} from '@/interfaces/events/AdventCalendar'
import { defineStore } from 'pinia'
import { internalAxios } from '@/plugins/vueAxios'
import {
  adventLoadCalendarEndpoint as LOAD_CALENDAR_EP,
  adventClaimEndpoint as CLAIM_EP,
  adventOpenCurrentEndpoint as OPEN_CURRENT_EP,
} from '@/globalVariables'

interface AdventCalendarState {
  windows: CalendarWindowType[]
  selectedWindow: CalendarWindowType | null
  missedCost: number
  currentWindow: number
  timeToNextDay: number | null
}

export const useAdventCalendarStore = defineStore('adventCalendar', {
  state: (): AdventCalendarState => ({
    windows: [],
    currentWindow: 0,
    selectedWindow: null,
    missedCost: 0,
    timeToNextDay: null,
  }),

  actions: {
    async loadCalendar(): Promise<void> {
      this.windows = []
      this.currentWindow = 0
      this.missedCost = 0
      this.timeToNextDay = null

      try {
        const response = await internalAxios.get<{}, LoadCalendarResponse>(LOAD_CALENDAR_EP)

        this.windows = response.windows ?? []
        this.currentWindow = response.currentWindow ?? 0
        this.missedCost = response.missedCost ?? 0
        this.timeToNextDay = response?.timeToNextDay ?? null
      } catch (error) {
        console.error('loadCalendar failed:', error)
      }
    },

    async afterVideo(windowNumber: number): Promise<void> {
      try {
        await this.loadCalendar()
        this.selectByNumber(windowNumber)
      } catch (error) {
        console.error('afterVideo failed:', error)
      }
    },

    selectWindow(w: CalendarWindowType, replace: boolean = false): void {
      const isSelected = this.selectedWindow && w.order === this.selectedWindow.order
      this.selectedWindow = isSelected && !replace ? null : w
      if (this.selectedWindow && w.status === WindowStatus.Active && !w.reward) this.openWindow()
    },

    selectByNumber(no: number): void {
      const windowData = this.windows.find((w: CalendarWindowType): boolean => w.order === no)
      if (windowData) this.selectWindow(windowData, true)
    },

    async openWindow(): Promise<void> {
      try {
        const response = await internalAxios.get<{}, { reward: CalendarWindowType['reward'] }>(
          OPEN_CURRENT_EP,
        )

        if (!response) return

        this.windows = this.windows.map((w: CalendarWindowType): CalendarWindowType => {
          const isCurrent = this.currentWindow === w.order
          if (!isCurrent) return w
          else
            return {
              ...w,
              reward: response.reward,
            }
        })

        this.selectByNumber(this.currentWindow)
      } catch (error: unknown) {
        console.error('openWindow failed:', error)
      }
    },

    async claimReward(): Promise<void> {
      if (!this.selectedWindow) return

      try {
        const response = await internalAxios.post<{}, boolean, number>(
          CLAIM_EP + this.selectedWindow.order,
        )

        if (!response) return

        this.windows = this.windows.map((w: CalendarWindowType): CalendarWindowType => {
          const isSelected = this.selectedWindow.order === w.order
          if (!isSelected) return w
          else
            return {
              ...w,
              status: WindowStatus.Claimed,
            }
        })
        this.selectByNumber(this.selectedWindow.order)
      } catch (error: unknown) {
        console.error('claimReward failed:', error)
      }
    },
  },
})
